import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { DetailHeader } from '../../components/layout/DetailHeader.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { FieldGrid } from '../../components/ui/FieldGrid.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate, formatDateTime, formatNumber } from '../../utils/formatters.js';
import { chfEquivalent } from '../../utils/fx.js';

import { AttachmentsPanel } from '../../components/data/AttachmentsPanel.jsx';
import { InvoiceMatchCard } from './InvoiceMatchCard.jsx';
import { InvoiceSendModal } from './InvoiceSendModal.jsx';
import { PayInvoiceModal } from './PayInvoiceModal.jsx';
import { CreditNoteCreateModal } from './CreditNoteCreateModal.jsx';

export function InvoiceDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sendOpen, setSendOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [creditTarget, setCreditTarget] = useState(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundRate, setRefundRate] = useState('');
  const [refundError, setRefundError] = useState(null);

  const invoice = useQuery({
    queryKey: ['finance', 'invoices', id],
    queryFn: () => financeApi.getInvoice(id).then((r) => r.items),
  });

  // FX refunds need a rate; prefill from the latest stored rate (hooks stay
  // top-level - the refund dialog is part of this page, so the query is
  // enabled-gated instead of conditionally mounted).
  const invCurrency = invoice.data?.currency;
  const isFx = Boolean(invCurrency && invCurrency !== 'CHF');
  const latestRate = useQuery({
    queryKey: ['finance', 'exchange-rate-latest', invCurrency],
    queryFn: () =>
      financeApi
        .listExchangeRates({ currency: invCurrency, limit: 1 })
        .then((r) => (r.items || [])[0] || null),
    enabled: isFx,
  });
  useEffect(() => {
    const fetched = latestRate.data?.rate;
    if (!fetched) {
      return;
    }
    setRefundRate((prev) => (prev === '' ? String(Number(fetched)) : prev));
    // invoice identity re-fires the prefill when the page shows another
    // invoice - structural sharing keeps latestRate.data identical, so it
    // alone would not re-trigger after the rate field reset.
  }, [latestRate.data, invoice.data?.id]);
  const accounts = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: () => financeApi.listAccounts().then((r) => r.items || []),
  });
  const creditNotes = useQuery({
    queryKey: ['finance', 'invoices', id, 'credit-notes'],
    queryFn: () => financeApi.listInvoiceCreditNotes(id).then((r) => r.items || []),
  });
  const postCn = useMutation({
    mutationFn: (cnId) => financeApi.postCreditNote(cnId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'invoices', id] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'invoices', id, 'credit-notes'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'credit-notes'] });
    },
  });
  const refund = useMutation({
    mutationFn: (body) => financeApi.refundInvoice(id, body),
    onSuccess: () => {
      setRefundOpen(false);
      setRefundAmount('');
      setRefundError(null);
      queryClient.invalidateQueries({ queryKey: ['finance', 'invoices', id] });
    },
  });

  const post = useMutation({
    mutationFn: () => financeApi.postInvoice(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance', 'invoices', id] }),
  });

  if (invoice.isLoading) {
    return <section className="page"><Spinner /></section>;
  }
  if (invoice.error || !invoice.data) {
    return (
      <section className="page">
        <DetailHeader backTo="/finance/invoices" title={t('finance.invoices.title')} />
        <div className="alert danger">{invoice.error?.message || 'Not found'}</div>
      </section>
    );
  }

  const inv = invoice.data;
  const credited = Number(inv.creditedAmount || 0);
  const refunded = Number(inv.refundedAmount || 0);
  const outstanding = Math.max(0, Number(inv.totalAmount) - Number(inv.paidAmount) - credited);
  const creditBalance = Math.max(0, Number(inv.paidAmount) + credited - Number(inv.totalAmount) - refunded);
  const accountById = new Map((accounts.data || []).map((a) => [a.id, a]));

  const actions = [];
  actions.push(
    <Button key="print" variant="default" onClick={() => navigate(`/finance/invoices/${id}/print`)}>
      {t('invoice.print.viewPdf')}
    </Button>,
  );
  // Customers only get posted sales invoices - no drafts, no purchase docs.
  if (inv.type === 'SALES' && (inv.status === 'POSTED' || inv.status === 'PAID')) {
    actions.push(
      <Button key="email" variant="ghost" onClick={() => setSendOpen(true)}>
        {t('email.send')}
      </Button>,
    );
  }
  if (inv.status === 'DRAFT') {
    actions.push(
      <Button key="post" variant="primary" onClick={() => post.mutate()} disabled={post.isPending}>
        {t('finance.invoices.action.post')}
      </Button>,
    );
  }
  if (inv.status === 'POSTED' && outstanding > 0) {
    actions.push(
      <Button key="pay" variant="primary" onClick={() => setPayTarget(inv)}>
        {t('finance.invoices.action.pay')}
      </Button>,
    );
  }
  if ((inv.status === 'POSTED' || inv.status === 'PAID') && credited < Number(inv.totalAmount)) {
    actions.push(
      <Button key="credit" variant="default" onClick={() => setCreditTarget(inv)}>
        {t('creditNote.create.title')}
      </Button>,
    );
  }
  if (creditBalance > 0) {
    const refundLabel = inv.type === 'PURCHASE' ? t('invoice.refund.receiveAction') : t('invoice.refund.action');
    actions.push(
      <Button key="refund" variant="default" onClick={() => { setRefundAmount(creditBalance.toFixed(2)); setRefundOpen(true); }}>
        {refundLabel}
      </Button>,
    );
  }

  const sourceLink = inv.sourceType === 'SALES_ORDER' && inv.sourceId
    ? { label: t('detail.fromOrder'), to: `/sales/orders/${inv.sourceId}` }
    : inv.sourceType === 'PURCHASE_ORDER' && inv.sourceId
      ? { label: t('detail.fromPO'), to: `/inventory/purchase-orders/${inv.sourceId}` }
      : null;

  return (
    <section className="page">
      <DetailHeader
        backTo="/finance/invoices"
        title={inv.invoiceNumber}
        status={inv.status}
        actions={actions}
        metrics={[
          { label: t('detail.metric.total'), value: formatCurrency(inv.totalAmount, inv.currency) },
          { label: t('detail.metric.paid'), value: formatCurrency(inv.paidAmount, inv.currency) },
          { label: t('detail.metric.outstanding'), value: formatCurrency(outstanding, inv.currency) },
          ...(isFx
            ? [
                { label: t('invoice.fx.rate'), value: <span className="mono">{Number(inv.exchangeRate).toFixed(4)}</span> },
                // Explicit 'CHF' - the equivalent IS CHF, regardless of the
                // global display currency.
                { label: t('invoice.fx.chfEquivalent'), value: <span className="mono">{formatCurrency(chfEquivalent(inv.totalAmount, inv.exchangeRate), 'CHF')}</span> },
              ]
            : []),
          ...(inv.dueDate && inv.status === 'POSTED'
            ? [(() => {
                const days = Math.ceil((new Date(inv.dueDate) - new Date()) / 86400000);
                return days >= 0
                  ? { label: t('detail.metric.dueInDays'), value: days }
                  : {
                      label: t('detail.metric.overdueDays'),
                      value: <span style={{ color: 'var(--color-danger)' }}>{Math.abs(days)}</span>,
                    };
              })()]
            : []),
        ]}
      />

      <Card title={t('detail.header')}>
        <FieldGrid items={[
          { label: t('col.type'), value: <Badge variant="info">{t(`invoiceType.${inv.type}`)}</Badge> },
          { label: inv.type === 'SALES' ? t('finance.invoices.modal.customer') : t('finance.invoices.modal.vendor'),
            value: (inv.customerId || inv.vendorId || '').slice(0, 8) },
          { label: t('col.date'), value: formatDate(inv.invoiceDate) },
          { label: t('col.due'), value: formatDate(inv.dueDate) },
          { label: t('vat.net'), value: <span className="mono">{formatCurrency(inv.netAmount ?? inv.totalAmount, inv.currency)}</span> },
          { label: t('vat.tax'), value: <span className="mono">{formatCurrency(inv.taxAmount ?? 0, inv.currency)}</span> },
          { label: t('col.total'), value: <strong className="mono">{formatCurrency(inv.totalAmount, inv.currency)}</strong> },
          { label: t('col.paid'), value: <span className="mono">{formatCurrency(inv.paidAmount, inv.currency)}</span> },
          { label: t('detail.outstanding'), value: <strong className="mono">{formatCurrency(outstanding, inv.currency)}</strong> },
          { label: t('creditNote.credited'), value: <span className="mono">{formatCurrency(credited, inv.currency)}</span> },
          ...(creditBalance > 0 ? [{ label: t(inv.type === 'PURCHASE' ? 'invoice.refund.receiveBalance' : 'invoice.refund.balance'), value: <strong className="mono">{formatCurrency(creditBalance, inv.currency)}</strong> }] : []),
          { label: t('detail.notes'), value: inv.notes },
          { label: t('detail.postedAt'), value: formatDateTime(inv.postedAt) },
          { label: t('detail.created'), value: formatDateTime(inv.createdAt) },
        ]} />
      </Card>

      {sourceLink && (
        <Card title={t('detail.related')}>
          <FieldGrid items={[
            { label: sourceLink.label, value: <Link to={sourceLink.to}>{inv.sourceId.slice(0, 8)}</Link> },
          ]} />
        </Card>
      )}

      <InvoiceMatchCard invoice={inv} />

      <Card title={t('detail.lines')}>
        <Table
          rows={inv.lines || []}
          getRowKey={(row) => row.id}
          columns={[
            {
              key: 'account',
              label: t('detail.lineAccount'),
              render: (r) => {
                const a = accountById.get(r.accountId);
                return a ? `${a.code} - ${a.name}` : r.accountId.slice(0, 8);
              },
            },
            { key: 'description', label: t('detail.lineDescription') },
            { key: 'quantity', label: t('detail.lineQty'), align: 'right', render: (r) => formatNumber(r.quantity) },
            { key: 'unitPrice', label: t('detail.lineUnitPrice'), align: 'right', render: (r) => formatCurrency(r.unitPrice, inv.currency) },
            { key: 'taxRate', label: t('vat.rateCol'), align: 'right', render: (r) => `${Number(r.taxRate || 0).toFixed(1)} %` },
            { key: 'lineAmount', label: t('detail.lineTotal'), align: 'right', render: (r) => formatCurrency(r.lineAmount, inv.currency) },
          ]}
        />
      </Card>

      <AttachmentsPanel
        queryKey={['finance', 'invoice-attachments', id]}
        api={{
          list: () => financeApi.listInvoiceAttachments(id).then((r) => r.items.attachments),
          upload: (body) => financeApi.uploadInvoiceAttachment(id, body),
          remove: (attId) => financeApi.deleteAttachment(attId),
          download: (attId, name) => financeApi.downloadAttachment(attId, name),
        }}
      />

      {(creditNotes.data || []).length > 0 && (
        <Card title={t('creditNote.title')}>
          <Table
            rows={creditNotes.data}
            getRowKey={(row) => row.id}
            columns={[
              { key: 'creditNoteNumber', label: t('col.number'), render: (r) => <Link to={`/finance/credit-notes/${r.id}/print`}>{r.creditNoteNumber}</Link> },
              { key: 'creditDate', label: t('col.date'), render: (r) => formatDate(r.creditDate) },
              { key: 'totalAmount', label: t('col.total'), align: 'right', render: (r) => formatCurrency(r.totalAmount, r.currency) },
              { key: 'status', label: t('col.status'), render: (r) => <Badge status={r.status} /> },
              {
                key: 'actions', label: '',
                render: (r) => (r.status === 'DRAFT'
                  ? <Button size="sm" variant="primary" disabled={postCn.isPending} onClick={() => postCn.mutate(r.id)}>{t('finance.invoices.action.post')}</Button>
                  : null),
              },
            ]}
          />
        </Card>
      )}

      <CreditNoteCreateModal invoice={creditTarget} onClose={() => setCreditTarget(null)} />

      {refundOpen && (
        <div className="alert" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end' }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="field-label">{t('invoice.refund.amount')}</span>
            <input className="input" type="number" min="0" step="0.01" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} />
          </label>
          {isFx && (
            <label className="field" style={{ flex: 1 }}>
              <span className="field-label">{t('invoice.fx.refundRate')}</span>
              <input className="input" type="number" min="0.000001" step="0.000001" value={refundRate} onChange={(e) => setRefundRate(e.target.value)} />
            </label>
          )}
          <Button
            variant="primary"
            disabled={refund.isPending}
            onClick={() => {
              setRefundError(null);
              if (isFx && !(Number(refundRate) > 0)) {
                setRefundError(t('invoice.fx.noRate', { currency: inv.currency }));
                return;
              }
              const body = { amount: refundAmount };
              if (isFx) body.refundRate = Number(refundRate);
              refund.mutate(body);
            }}
          >
            {inv.type === 'PURCHASE' ? t('invoice.refund.receiveAction') : t('invoice.refund.action')}
          </Button>
          <Button variant="ghost" onClick={() => { setRefundOpen(false); setRefundError(null); }}>{t('common.cancel')}</Button>
        </div>
      )}
      {refundError && <div className="alert danger">{refundError}</div>}
      {refund.isError && <div className="alert danger">{refund.error?.message}</div>}

      <PayInvoiceModal invoice={payTarget} onClose={() => setPayTarget(null)} />
      <InvoiceSendModal invoice={sendOpen ? inv : null} onClose={() => setSendOpen(false)} />
    </section>
  );
}
